import {
  useStatusChangedFocusAnalytics,
  useStepStartedFocusAnalytics,
} from 'features/application/hooks/useStepStartedFocusAnalytics';
import React, { useCallback, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import Button from 'shared/components/vxl/Button/Button';
import BodyBaseText from 'shared/components/vxl/typography/primary/body/BodyBaseText';
import TitleXLText from 'shared/components/vxl/typography/primary/title/TitleXLText';
import { useViewDidFocusScreenAnalyticsEvent } from 'shared/hooks/analytics/useViewDidFocusAnalytics';
import { useBackHandler } from 'shared/hooks/useBackHandler/useBackHandler';
import { AuthContext, AuthContextProps } from 'shared/providers/auth';
import theme from 'shared/styles/theme';

const ApplicationDeclined = ({
  isReactivation,
  isReapplication,
}: {
  isReactivation?: boolean;
  isReapplication?: boolean;
}) => {
  useViewDidFocusScreenAnalyticsEvent({
    key: 'applicationDeclined',
    customAttributes: {
      ...(isReactivation && {
        reactivation_flow: 'true',
      }),
      ...(isReapplication && {
        reapplication_flow: 'true',
      }),
    },
  });

  useStepStartedFocusAnalytics({
    analyticsId: 'applicationDeclined',
    customAttributes: {
      ...(isReactivation && {
        reactivation_flow: 'true',
      }),
      ...(isReapplication && {
        reapplication_flow: 'true',
      }),
    },
  });

  useStatusChangedFocusAnalytics({
    analyticsId: 'statusChanged',
    customAttributes: {
      status: 'declined',
      ...(isReactivation && {
        reactivation_flow: 'true',
      }),
      ...(isReapplication && {
        reapplication_flow: 'true',
      }),
    },
  });

  const { t: translatedText } = useTranslation('signup', {
    keyPrefix: 'declined',
  });

  const { handleLogout } = useContext<AuthContextProps>(AuthContext);

  const handleBackButtonAction = useCallback(() => {
    void handleLogout();
    return true;
  }, [handleLogout]);

  useBackHandler(handleBackButtonAction);

  const { bottom, top } = useSafeAreaInsets();

  return (
    <View
      style={[styles.container, { paddingBottom: bottom, paddingTop: top }]}
    >
      <View style={styles.content}>
        <TitleXLText>{translatedText('title')}</TitleXLText>
        <BodyBaseText numberOfLines={0}>
          {translatedText('description')}
        </BodyBaseText>
      </View>
      <View style={styles.buttonContainer}>
        <Button
          label={translatedText('close')}
          onPress={handleLogout}
          size="LARGE"
          type="PRIMARY"
          width="FULL"
        />
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  buttonContainer: {
    borderTopColor: theme.color.border.primarySolid,
    borderTopWidth: 1,
    gap: theme.spacing.m,
    padding: theme.spacing.m,
  },
  container: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
    justifyContent: 'space-between',
  },
  content: {
    gap: theme.spacing.m,
    paddingHorizontal: theme.spacing.m,
    paddingTop: 47,
  },
});

export default ApplicationDeclined;
